<template>
  <v-container fluid class="wallet-page" v-resize="onResize">
    <!-- Balance hero card -->
    <div class="balance-card">
      <div class="balance-card__top">
        <div class="balance-card__account">
          <v-icon color="white" class="balance-card__account-icon">account_balance_wallet</v-icon>
          <div class="balance-card__account-info">
            <span class="balance-card__label">Conta Digital</span>
            <span class="balance-card__name">
              {{ partner === 'establishment' ? account.fantasy_name : partnerName }}
            </span>
          </div>
        </div>
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="white"
          size="24"
          width="2"
        />
      </div>

      <div class="balance-card__balance">
        <span class="balance-card__balance-label">Saldo disponível</span>
        <span class="balance-card__balance-value">{{ $n(getBalance(), "currency") }}</span>
      </div>
    </div>

    <!-- Quick actions -->
    <div class="quick-actions">
      <button class="quick-action" type="button" @click="rechargeWallet">
        <span class="quick-action__icon quick-action__icon--recharge">
          <v-icon dark>add</v-icon>
        </span>
        <span class="quick-action__label">Recarregar</span>
      </button>

      <button class="quick-action" type="button" @click="withdrawForm = true">
        <span class="quick-action__icon quick-action__icon--withdraw">
          <v-icon dark>attach_money</v-icon>
        </span>
        <span class="quick-action__label">Sacar</span>
      </button>

      <button class="quick-action" type="button" @click="enableFilters">
        <span class="quick-action__icon quick-action__icon--filter">
          <v-icon dark>filter_list</v-icon>
        </span>
        <span class="quick-action__label">Filtrar</span>
      </button>

      <div class="quick-action quick-action--export">
        <export />
        <span class="quick-action__label">Exportar</span>
      </div>
    </div>

    <!-- Statement card -->
    <v-card class="statement-card" flat>
      <div class="statement-card__header">
        <v-icon class="statement-card__header-icon">receipt_long</v-icon>
        <span>Extrato</span>
      </div>
      <extract-table />
    </v-card>
    <v-layout row justify-center>
      <v-dialog v-model="withdrawForm" persistent max-width="560px">
        <v-card class="withdraw-modal">
          <v-form ref="form" v-model="valid" lazy-validation>
            <div class="withdraw-head">
              <div class="withdraw-head__icon">
                <v-icon color="white">attach_money</v-icon>
              </div>
              <span class="withdraw-head__title">Solicitação de Saque</span>
            </div>
            <v-card-text class="withdraw-body">
              <!-- Explicativo -->
              <div class="withdraw-info">
                <v-icon class="withdraw-info__icon">info</v-icon>
                <span>
                  O valor solicitado será transferido para a
                  <strong>conta bancária cadastrada</strong> abaixo, após a
                  aprovação do backoffice. Confira os dados de destino antes de
                  confirmar.
                </span>
              </div>

              <!-- Confirmação da conta de destino -->
              <div v-if="hasBankAccount" class="dest-account">
                <div class="dest-account__label">
                  <v-icon class="dest-account__label-icon">account_balance</v-icon>
                  Conta de destino
                </div>
                <div class="dest-account__bank">{{ bankAccountName }}</div>
                <div class="dest-account__grid">
                  <div class="dest-account__item">
                    <span class="dest-account__key">Agência</span>
                    <span class="dest-account__val">{{ agencyDisplay }}</span>
                  </div>
                  <div class="dest-account__item">
                    <span class="dest-account__key">Conta</span>
                    <span class="dest-account__val">{{ numberDisplay }}</span>
                  </div>
                  <div class="dest-account__item">
                    <span class="dest-account__key">Tipo</span>
                    <span class="dest-account__val">{{ kindDisplay }}</span>
                  </div>
                  <div class="dest-account__item dest-account__item--full">
                    <span class="dest-account__key">Titular</span>
                    <span class="dest-account__val">{{ bankAccount.holder_name || account.name }}</span>
                  </div>
                </div>
              </div>

              <div v-else class="dest-account dest-account--empty">
                <v-icon class="dest-account__warn-icon">warning</v-icon>
                <span>
                  Nenhuma conta bancária cadastrada. Cadastre uma conta de
                  destino antes de solicitar o saque.
                </span>
              </div>

              <v-text-field
                class="withdraw-amount"
                label="Valor do saque"
                prepend-inner-icon="payments"
                v-model.lazy="amount"
                v-money="money"
                :rules="amountRules"
                required
              ></v-text-field>
              <small class="withdraw-required">*campo obrigatório</small>
            </v-card-text>
            <v-card-actions class="withdraw-actions">
              <v-btn flat @click="withdrawForm = false">Cancelar</v-btn>
              <v-spacer></v-spacer>
              <v-btn
                class="white--text"
                color="green darken-1"
                depressed
                :disabled="!hasBankAccount"
                @click="validate"
              >
                Confirmar saque
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
    </v-layout>
    <filters />
    <div class="text-center ma-2">
      <v-snackbar v-model="notificationBar" :color="withdrawSuccess ? 'success' : 'error'">
        <div v-if="withdrawSuccess">
          <v-icon left>check_circle</v-icon>
          Solicitação de saque enviada com sucesso!
          <br>
          <small>Saldo atualizado. Aguarde a aprovação do backoffice.</small>
        </div>
        <div v-else-if="withdrawError">
          <v-icon left>error</v-icon>
          Erro na solicitação: {{ withdrawErrorMessage }}
        </div>
        <div v-else>
          {{ "Transação: " + this.withdraw.transaction }}
        </div>

        <template v-slot:action="{ attrs }">
          <v-btn color="white" text v-bind="attrs" @click="notificationBar = false">
            Fechar
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </v-container>
</template>
<script>
// import { EventBus } from "@/services/event-bus";
import { VMoney } from "v-money";
import { mask } from "vue-the-mask";
import { mapGetters } from "vuex";

export default {
  name: "Wallet",
  created() {
    this.$store.dispatch("account/getAccount");
    this.$store.dispatch("wallet/getExtract", this.$store.getters["wallet/filterParams"]);
  },
  directives: { money: VMoney, mask },
  data: () => ({
    withdrawForm: false,
    isMobile: false,
    amount: "",
    amountRules: [v => !!v || "Valor deve ser válido"],
    valid: false,
    money: {
      decimal: ",",
      thousands: ".",
      prefix: "R$ ",
      precision: 2,
      masked: false,
    },
  }),
  components: {
    ExtractTable: () => import("@/components/desktop/wallet/ExtractTable.vue"),
    Filters: () => import("@/components/desktop/wallet/Filters.vue"),
    Export: () => import("./Export.vue"),
  },
  computed: {
    ...mapGetters({
      account: "account/account",
      partner: "account/partner",
      partnerName: "account/partnerName",
      loading: "wallet/loadingData",
      notificationBar: "wallet/notificationBar",
      withdraw: "wallet/withdraw",
      withdrawSuccess: "wallet/withdrawSuccess",
      withdrawError: "wallet/withdrawError",
      withdrawErrorMessage: "wallet/withdrawErrorMessage",
    }),
    bankAccount() {
      return (this.account && this.account.bank_account_attributes) || {};
    },
    hasBankAccount() {
      const b = this.bankAccount;
      return !!(b.number || b.pix_key);
    },
    bankAccountName() {
      const b = this.bankAccount;
      if (b.bank_name) {
        return b.bank_code ? `${b.bank_code} - ${b.bank_name}` : b.bank_name;
      }
      return "Banco não informado";
    },
    agencyDisplay() {
      const b = this.bankAccount;
      if (!b.agency) return "—";
      return b.agency_digit ? `${b.agency}-${b.agency_digit}` : b.agency;
    },
    numberDisplay() {
      const b = this.bankAccount;
      if (!b.number) return "—";
      return b.number_digit ? `${b.number}-${b.number_digit}` : b.number;
    },
    kindDisplay() {
      const kinds = {
        cc: "Conta Corrente",
        checking: "Conta Corrente",
        corrente: "Conta Corrente",
        conta_corrente: "Conta Corrente",
        cp: "Conta Poupança",
        savings: "Conta Poupança",
        poupanca: "Conta Poupança",
        poupança: "Conta Poupança",
      };
      const k = this.bankAccount.kind;
      if (!k) return "—";
      return kinds[String(k).toLowerCase()] || k;
    },
  },
  methods: {
    onResize() {
      if (window.innerWidth < 769) this.isMobile = true;
      else this.isMobile = false;
    },
    getBalance() {
      return this.$store.state.wallet.wallet.balance;
    },
    rechargeWallet() {
      this.$router.push({ name: "recharge-wallet" });
    },
    activeWithdraw() {
      this.withdrawForm = true;
    },
    validate() {
      try {
        if (this.$refs.form.validate()) {
          const withdraw = {
            amount: this.amount.replace(/[^\d]+/g, "") / 100,
          };
          this.$store.dispatch("wallet/withdrawRequest", withdraw);
          this.withdrawForm = false;
          this.$refs.form.reset();
        }
      } catch (err) {
        console.error("Erro ao processar saque:", err);
      }
    },
    enableFilters() {
      this.$store.dispatch("wallet/showFilters", true);
    },
  },
  watch: {
    notificationBar(value) {
      this.notificationBar = value;
    },
    withdrawSuccess(newValue) {
      if (newValue) {
        // Resetar a flag após processar
        setTimeout(() => {
          this.$store.commit("wallet/WITHDRAW_SUCCESS", false);
        }, 3000);
      }
    },
    withdrawError(newValue) {
      if (newValue) {
        // Resetar a flag após processar
        setTimeout(() => {
          this.$store.commit("wallet/WITHDRAW_ERROR", false);
        }, 3000);
      }
    },
  },
};
</script>

<style scoped>
.wallet-page {
  max-width: 920px;
  margin: 0 auto;
  padding: 16px 12px 32px;
}

/* Balance hero card */
.balance-card {
  background: linear-gradient(135deg, #1e6cff 0%, #0a3fb5 100%);
  border-radius: 20px;
  padding: 22px 24px;
  color: #fff;
  box-shadow: 0 10px 28px rgba(13, 71, 161, 0.28);
}

.balance-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.balance-card__account {
  display: flex;
  align-items: center;
  gap: 12px;
}

.balance-card__account-icon {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 8px;
}

.balance-card__account-info {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.balance-card__label {
  font-size: 12px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.balance-card__name {
  font-size: 16px;
  font-weight: 600;
}

.balance-card__balance {
  margin-top: 22px;
  display: flex;
  flex-direction: column;
}

.balance-card__balance-label {
  font-size: 13px;
  opacity: 0.85;
}

.balance-card__balance-value {
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

/* Quick actions */
.quick-actions {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 28px;
  margin: 22px 0 10px;
}

.quick-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  padding: 4px;
}

.quick-action__icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.quick-action:hover .quick-action__icon {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
}

.quick-action__icon--recharge {
  background: linear-gradient(135deg, #ff9d2e, #f57c00);
}
.quick-action__icon--withdraw {
  background: linear-gradient(135deg, #2ec87a, #15a35a);
}
.quick-action__icon--filter {
  background: linear-gradient(135deg, #5b8def, #2f6fe0);
}

.quick-action__label {
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
}

.quick-action--export {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

/* Statement card */
.statement-card {
  margin-top: 10px;
  border-radius: 18px !important;
  box-shadow: 0 6px 22px rgba(17, 24, 39, 0.06) !important;
  overflow: hidden;
}

.statement-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 20px 4px;
  font-size: 17px;
  font-weight: 700;
  color: #1f2933;
}

.statement-card__header-icon {
  color: #1e6cff !important;
}

/* Withdraw modal */
.withdraw-modal {
  border-radius: 18px !important;
  overflow: hidden;
}

.withdraw-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid #eef1f5;
}

.withdraw-head__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #2ec87a, #15a35a);
  box-shadow: 0 6px 14px rgba(21, 163, 90, 0.35);
}

.withdraw-head__title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2933;
}

.withdraw-body {
  padding: 20px 22px 8px !important;
}

.withdraw-info {
  display: flex;
  gap: 10px;
  background: #eef5ff;
  border: 1px solid #d6e6ff;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 13.5px;
  line-height: 1.45;
  color: #2c3e50;
}

.withdraw-info__icon {
  color: #1e6cff !important;
  font-size: 20px !important;
}

/* Destination account card */
.dest-account {
  margin-top: 16px;
  border: 1px solid #e6e9ef;
  border-radius: 14px;
  padding: 14px 16px;
  background: #fafbfc;
}

.dest-account__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8a94a6;
}

.dest-account__label-icon {
  font-size: 18px !important;
  color: #15a35a !important;
}

.dest-account__bank {
  font-size: 16px;
  font-weight: 700;
  color: #1f2933;
  margin: 6px 0 12px;
}

.dest-account__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.dest-account__item {
  display: flex;
  flex-direction: column;
}

.dest-account__item--full {
  grid-column: 1 / -1;
}

.dest-account__key {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #9aa3b2;
}

.dest-account__val {
  font-size: 14.5px;
  font-weight: 600;
  color: #263238;
  margin-top: 2px;
}

.dest-account--empty {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff7e6;
  border-color: #ffe0a3;
  color: #8a6d3b;
  font-size: 13.5px;
  line-height: 1.4;
}

.dest-account__warn-icon {
  color: #e0a12e !important;
}

.withdraw-amount {
  margin-top: 18px;
}

.withdraw-required {
  color: #9aa3b2;
}

.withdraw-actions {
  padding: 8px 18px 18px !important;
}

@media screen and (max-width: 600px) {
  .balance-card__balance-value {
    font-size: 28px;
  }
  .quick-actions {
    gap: 16px;
  }
  .dest-account__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
